import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Alert, Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Loading } from 'components/Loading';
import { clearResponseMessages, refreshUsers } from 'store/actions';
import {
  selectMetadata,
  selectResponseMessages,
  selectToken,
} from 'store/selectors';

import { listStyle, responseMessagesStyle } from './UserRefresh.style';

const VisuallyHiddenInput = styled('input')({
  display: 'none',
});

const UserRefresh = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const md = useSelector(selectMetadata);
  const responseMessages = useSelector(selectResponseMessages);

  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [provisionalDone, setProvisionalDone] = useState(false);
  const [provisionalLoading, setProvisionalLoading] = useState(false);

  React.useEffect(() => {
    if (responseMessages.length > 0 && responseMessages[0] !== '') {
      setProvisionalLoading(false);
    }
  }, [dispatch, responseMessages, provisionalDone, provisionalLoading]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);
    setProvisionalDone(false);
    setMessage('');
    dispatch(clearResponseMessages());
  };

  const handleProvisionalRefresh = async () => {
    if (files.length !== 2) {
      setMessage('Please select exactly two files.');
      return;
    }

    const filenames = files.map((f) => f.name);
    if (!filenames.includes('imdea.yaml') || !filenames.includes('list.yaml')) {
      setMessage("Files must be named 'imdea.yaml' and 'list.yaml'.");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    try {
      setProvisionalLoading(true);
      setProvisionalDone(true);
      dispatch(refreshUsers(formData, token));
      setMessage('Provisional Refresh Done!');
    } catch (err) {
      setProvisionalDone(false);
      setMessage(err.response?.data?.error || 'Provisional Refresh failed');
    }
  };

  const handleFinalRefresh = async () => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    try {
      dispatch(refreshUsers(formData, token, 'false'));
      setProvisionalDone(false);
      setFiles([]);
      setMessage('');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Final Refresh failed');
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Typography variant="h5">Update Users</Typography>
      <Typography variant="subtitle2" color="text.secondary">
        Last updated: {md.last_users_update ?? 'N/A'}
      </Typography>

      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}>
          Upload files
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileSelect}
            multiple
            accept=".yaml"
          />
        </Button>

        {files.length > 0 && (
          <Box>
            <Typography>
              Selected files: {files.map((file) => file.name).join(', ')}
            </Typography>
          </Box>
        )}
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleProvisionalRefresh}
        disabled={files.length === 0}>
        Provisional Update
      </Button>

      {provisionalLoading && <Loading />}
      {responseMessages.length > 0 && responseMessages[0] !== '' && (
        <Box sx={responseMessagesStyle}>
          <Typography variant="subtitle1">Final Actions:</Typography>
          <ul style={listStyle}>
            {responseMessages.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </Box>
      )}

      <Button
        variant="contained"
        color="secondary"
        onClick={handleFinalRefresh}
        disabled={!provisionalDone || provisionalLoading}>
        Final Update
      </Button>

      {message && !provisionalLoading && (
        <Alert severity={message.includes('Done') ? 'success' : 'error'}>
          {message}
        </Alert>
      )}
    </Box>
  );
};

export default UserRefresh;
